// Numbered table citations (#1296).
//
// The model writes `[Table: <label>]` into its markdown. Table names are now
// long and descriptive, so rendering the label inline breaks reading. This
// module is the ONE place that parses those tokens and assigns them numbers,
// so the marker in the text, the tooltip, the click target and the reference
// list under a card can never disagree.
//
// Numbering is a RENDER-TIME transform: nothing about the stored text changes,
// so cards written before this existed show numbers without regeneration.
//
// Token forms:
//   [Table: <label>]
//   [Table: <label> | cell: <row label> x <column label>]
//
// The cell is addressed by LABEL, not position, so it survives a table
// reflow. `x` is a plain letter x with a space either side. A cell that does
// not parse (missing row or column) degrades to a plain table citation.

/** A cited cell, addressed by the row and column LABELS so it survives a reflow. */
export interface CitationCell {
  row: string
  column: string
}

/** One citation as the reader sees it: a number, the table it names, and
 *  optionally the cell it points at. */
export interface CitationReference {
  /** 1-based marker number, in first-use order across the whole text. */
  number: number
  /** The table label the click resolves against — a trailing " Analysis" is
   *  dropped, the same resolution table citations have always used. */
  label: string
  /** Present only when the token carried a parseable cell. */
  cell?: CitationCell
}

/** A run of text, split at citation tokens. */
export type CitationSegment =
  | { kind: 'text'; text: string }
  | { kind: 'citation'; reference: CitationReference }

/** Matches a whole citation token, capturing its inner body. */
const CITATION_RE = /\[Table:\s*([^\]]+)\]/g
/** Same shape, as a capturing split so the token survives String.split. */
const CITATION_SPLIT_RE = /(\[Table:\s*[^\]]+\])/g
/** One whole token — the split parts are either a complete token or plain text. */
const WHOLE_CITATION_RE = /^\[Table:\s*([^\]]+)\]$/
/** `<label> | cell: <rest>` — the cell segment is optional and always last. */
const CELL_SEGMENT_RE = /^(.*?)\|\s*cell\s*:\s*(.+)$/i
/** `<row label> x <column label>`, splitting on the FIRST spaced x. */
const ROW_BY_COLUMN_RE = /^(.+?)\s+x\s+(.+)$/i

/** Drop the " Analysis" suffix the model sometimes appends to a table name. */
export function resolveCitationLabel(raw: string): string {
  const label = raw.trim()
  return label.endsWith(' Analysis') ? label.slice(0, -9).trim() : label
}

/** Parse the inside of one token. Returns null when there is no usable label,
 *  which is how a malformed token stays as plain text. */
function parseCitationBody(inner: string): { label: string; cell?: CitationCell } | null {
  let body = inner
  let cell: CitationCell | undefined

  const cellSegment = CELL_SEGMENT_RE.exec(inner)
  if (cellSegment) {
    body = cellSegment[1]
    const rowByColumn = ROW_BY_COLUMN_RE.exec(cellSegment[2].trim())
    if (rowByColumn) {
      const row = rowByColumn[1].trim()
      const column = rowByColumn[2].trim()
      if (row && column) cell = { row, column }
    }
  }

  const label = resolveCitationLabel(body)
  if (!label) return null
  return cell ? { label, cell } : { label }
}

/** Numbering shared by every marker in one card / message. */
export interface CitationNumbering {
  /** Split one run of text into plain text and numbered citations. */
  parse(text: string): CitationSegment[]
  /** Every distinct table cited, in first-use order. One entry per table, so
   *  repeated citations of the same table share a number and a line. */
  references: CitationReference[]
}

/**
 * Build the numbering for a whole text up front, so a marker rendered in the
 * middle of a paragraph and the reference list rendered underneath agree, and
 * so nothing depends on the order React happens to render text nodes in.
 */
export function createCitationNumbering(fullText: string): CitationNumbering {
  const numbers = new Map<string, number>()
  const references: CitationReference[] = []

  const numberFor = (label: string): number => {
    const existing = numbers.get(label)
    if (existing !== undefined) return existing
    const next = numbers.size + 1
    numbers.set(label, next)
    references.push({ number: next, label })
    return next
  }

  for (const match of fullText.matchAll(CITATION_RE)) {
    const parsed = parseCitationBody(match[1])
    if (parsed) numberFor(parsed.label)
  }

  const parse = (text: string): CitationSegment[] => {
    const parts = text.split(CITATION_SPLIT_RE)
    const segments: CitationSegment[] = []
    for (const part of parts) {
      if (part === '') continue
      const token = WHOLE_CITATION_RE.exec(part)
      const parsed = token ? parseCitationBody(token[1]) : null
      if (!token || !parsed) {
        // A malformed token stays as text. Merge it back into the run beside
        // it so callers see one string, not a seam where a token nearly was.
        const previous = segments[segments.length - 1]
        if (previous?.kind === 'text') previous.text += part
        else segments.push({ kind: 'text', text: part })
        continue
      }
      segments.push({
        kind: 'citation',
        reference: parsed.cell
          ? { number: numberFor(parsed.label), label: parsed.label, cell: parsed.cell }
          : { number: numberFor(parsed.label), label: parsed.label },
      })
    }
    return segments
  }

  return { parse, references }
}

/** Hover text for a marker: the full table name, plus the cell when present. */
export function citationTooltip(reference: CitationReference): string {
  if (!reference.cell) return reference.label
  return `${reference.label} — ${reference.cell.row} x ${reference.cell.column}`
}

/** The filter half of a reference-list line. */
export function citationReferenceFilter(filter: string | undefined, filterDropped?: boolean): string {
  if (filter) return filter
  if (filterDropped) return 'no filter — this table is from a different job than the dashboard filter'
  return 'no filter'
}
